import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import workCaseApi from '../../services/workCaseApi';
import CreateWorkCaseModal from './CreateWorkCaseModal';
import WorkCaseList from './WorkCaseList';
import WorkCaseDetail from './WorkCaseDetail';
import WorkCaseStatistics from './WorkCaseStatistics';

const WorkCaseManagement = ({ viewMode: viewModeProp }) => {
  const { user } = useAuth();
  const [workCases, setWorkCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    priority: '',
    viewMode: viewModeProp || 'assigned',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Load work cases
  const loadWorkCases = async () => {
    try {
      setLoading(true);
      const response = await workCaseApi.getMyWorkCases({
        ...filters,
        view: filters.viewMode === 'created' ? 'created' : 'assigned'
      });
      setWorkCases(response.cases);
      setPagination(response.pagination);
      setError(null);
    } catch (err) {
      setError(err.message || 'Lỗi khi tải danh sách work cases');
    } finally {
      setLoading(false);
    }
  };

  // Load work cases khi component mount hoặc filters thay đổi
  useEffect(() => {
    loadWorkCases();
  }, [filters]);

  // Đồng bộ viewMode từ prop (nếu được truyền bởi route con)
  useEffect(() => {
    if (viewModeProp && viewModeProp !== filters.viewMode) {
      setFilters(prev => ({ ...prev, viewMode: viewModeProp, page: 1 }));
    }
  }, [viewModeProp]);

  // Tạo work case mới
  const handleCreateWorkCase = async (workCaseData) => {
    try {
      await workCaseApi.createWorkCase(workCaseData);
      setShowCreateModal(false);
      loadWorkCases(); // Reload danh sách
    } catch (err) {
      setError(err.message || 'Lỗi khi tạo work case');
    }
  };

  // Cập nhật status assignment
  const handleUpdateAssignmentStatus = async (workCaseId, assigneeId, status, notes) => {
    try {
      const res = await workCaseApi.updateAssignmentStatus(workCaseId, assigneeId, { status, notes });
      // Cập nhật optimistic UI
      setWorkCases(prev => prev.map(wc => wc.id === workCaseId ? { ...wc, assignment_status: status } : wc));
      if (selectedCase) {
        const currentId = selectedCase.workCase?.id || selectedCase.id;
        if (currentId === workCaseId) {
          setSelectedCase(prev => {
            const wc = prev.workCase || prev;
            const nextWc = { ...wc };
            if (wc.assignee_id === assigneeId) {
              nextWc.status = status; // giữ status thực tế (completed) theo enum DB
            }
            const assignments = (prev.assignments || []).map(a => a.assignee_id === assigneeId ? { ...a, status, completed_at: status === 'completed' ? new Date().toISOString() : a.completed_at } : a);
            // Nếu người nhận vừa hoàn thành, đặt workCase.status = 'under_review' để chờ duyệt
            if (status === 'completed') {
              nextWc.status = 'under_review';
            }
            return { ...prev, workCase: nextWc, assignments };
          });
        }
      }
    } catch (err) {
      setError(err.message || 'Lỗi khi cập nhật status');
    }
  };

  // Approve/Reject work case
  const handleApproveReject = async (workCaseId, action, notes) => {
    try {
      await workCaseApi.approveRejectWorkCase(workCaseId, { action, notes });
      // Cập nhật optimistic UI
      const nextStatus = action === 'approve' ? 'approved' : 'rejected';
      setWorkCases(prev => prev.map(wc => wc.id === workCaseId ? { ...wc, status: nextStatus } : wc));
      if (selectedCase) {
        const currentId = selectedCase.workCase?.id || selectedCase.id;
        if (currentId === workCaseId) {
          setSelectedCase(prev => ({
            ...prev,
            workCase: { ...(prev.workCase || prev), status: nextStatus }
          }));
        }
      }
    } catch (err) {
      setError(err.message || 'Lỗi khi approve/reject work case');
    }
  };

  // Thêm note
  const handleAddNote = async (workCaseId, note) => {
    try {
      await workCaseApi.addNote(workCaseId, { note });
      if (selectedCase && selectedCase.id === workCaseId) {
        // Reload chi tiết case nếu đang xem
        const response = await workCaseApi.getWorkCaseDetail(workCaseId);
        setSelectedCase(response.data);
      }
    } catch (err) {
      setError(err.message || 'Lỗi khi thêm note');
    }
  };

  // Xem chi tiết work case
  const handleViewDetail = async (workCaseId) => {
    try {
      const response = await workCaseApi.getWorkCaseDetail(workCaseId);
      setSelectedCase(response.data);
    } catch (err) {
      setError(err.message || 'Lỗi khi tải chi tiết work case');
    }
  };

  // Đóng chi tiết
  const handleCloseDetail = () => {
    setSelectedCase(null);
  };

  // Thay đổi filters
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1 // Reset về trang đầu khi filter
    }));
  };

  // Thay đổi trang
  const handlePageChange = (page) => {
    setFilters(prev => ({
      ...prev,
      page
    }));
  };

  // Đóng modal tạo case
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
               <h1 className="text-3xl font-bold text-gray-900">Quản lý Work Cases</h1>
              <p className="mt-2 text-gray-600">
                Tạo và quản lý các công việc được giao
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              + Tạo Work Case
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
              <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => handleFilterChange({ page: 1 }) || setFilters(prev => ({ ...prev, viewMode: 'created' }))}
              className={`px-4 py-2 text-sm font-medium ${filters.viewMode === 'created' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            >
              Tôi đã giao
            </button>
            <button
              onClick={() => handleFilterChange({ page: 1 }) || setFilters(prev => ({ ...prev, viewMode: 'assigned' }))}
                  className={`px-4 py-2 text-sm font-medium border-l border-gray-200 ${filters.viewMode !== 'created' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            >
              Được giao cho tôi
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Lỗi</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError(null)}
                  className="inline-flex text-red-400 hover:text-red-600"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Work Case List */}
          <div className="lg:col-span-2">
            <WorkCaseList
              workCases={workCases}
              loading={loading}
              filters={{ ...filters }}
              pagination={pagination}
              onFilterChange={handleFilterChange}
              onPageChange={handlePageChange}
              onViewDetail={handleViewDetail}
              onUpdateStatus={handleUpdateAssignmentStatus}
              onApproveReject={handleApproveReject}
              currentUser={user}
            />
          </div>

          {/* Statistics Sidebar */}
          <div className="lg:col-span-1">
            <WorkCaseStatistics viewMode={filters.viewMode} />
          </div>
        </div>

        {/* Create Work Case Modal */}
        {showCreateModal && (
          <CreateWorkCaseModal
            onClose={handleCloseCreateModal}
            onSubmit={handleCreateWorkCase}
            currentUser={user}
          />
        )}

        {/* Work Case Detail Modal */}
          {selectedCase && (
          <WorkCaseDetail
            workCase={selectedCase}
            onClose={handleCloseDetail}
            onUpdateStatus={handleUpdateAssignmentStatus}
            onApproveReject={handleApproveReject}
            onAddNote={handleAddNote}
            currentUser={user}
          />
        )}
      </div>
    </div>
  );
};

export default WorkCaseManagement;
